import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DocumentText1, Calendar, User, Clock, Edit2 } from "iconsax-react";
import { CheckIcon } from "@/shared/assets/icons";
import { ModalForm, Button, Prompt, Skeleton, Badge, FileViewer } from "@/shared/ui";
import { extractErrorMessage, formatDateForDisplay, formatMoneyKzt } from "@/shared/utils";
import { DetailFieldRow } from "@/features/hr/shared";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import { useGetMedicalLeaveQuery, useAcknowledgeMedicalLeaveMutation } from "../api";
import { LEAVE_STATUS_MAP } from "../consts/statuses";
import CloseMedicalLeaveModal from "./CloseMedicalLeaveModal";

interface Props {
  leaveId: number;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function MedicalLeaveDetailsModal({ leaveId, onClose, onSuccess }: Props) {
  const { t } = useTranslation("LeaveApplicationsPage");
  const { hasPermission } = useAuth();
  const canReview = hasPermission(PERMISSIONS.HR.APPROVE_LEAVE);

  const { data: leave, isLoading } = useGetMedicalLeaveQuery(leaveId);

  const [acknowledge, { isLoading: isAcknowledging }] = useAcknowledgeMedicalLeaveMutation();

  const [prompt, setPrompt] = useState<{ title: string; text: string; variant?: "success" | "error" } | null>(null);
  const [showClose, setShowClose] = useState(false);

  function handlePromptClose() {
    const wasSuccess = prompt?.variant === "success";
    setPrompt(null);
    if (wasSuccess) onSuccess?.();
  }

  async function handleAcknowledge() {
    try {
      await acknowledge(leaveId).unwrap();
      setPrompt({ title: t("actions.acknowledgeSuccessTitle"), text: t("actions.acknowledgeSuccessText"), variant: "success" });
    } catch (err: unknown) {
      setPrompt({ title: t("messages.errorTitle"), text: extractErrorMessage(err, "actions.acknowledgeError", t), variant: "error" });
    }
  }

  function renderStatusBadge() {
    if (!leave) return null;
    if (leave.status === "draft" && leave.application_review_note) {
      return <Badge variant="soft" color="notice" text={t("statusLabels.revision_requested")} icon={<Edit2 size={14} color="currentColor" />} />;
    }
    const config = LEAVE_STATUS_MAP[leave.status];
    const label =
      leave.status === "active"
        ? "statusLabels.active_medical"
        : leave.status === "completed"
          ? "statusLabels.completed_medical"
          : config.label;
    return <Badge variant="soft" color={config.color} text={t(label)} icon={config.icon} />;
  }

  function renderFooter() {
    if (isLoading || !leave || !canReview) return undefined;
    const status = leave.status;

    const buttons: React.ReactNode[] = [];
    if (status === "pending_review") {
      buttons.push(
        <Button key="ack" variant="primary" size="huge" className="w-full" disabled={isAcknowledging} onClick={handleAcknowledge}>
          <CheckIcon />
          {t("actions.acknowledge")}
        </Button>,
      );
    } else if (status === "active") {
      buttons.push(
        <Button key="close" variant="primary" size="huge" className="w-full" onClick={() => setShowClose(true)}>
          <CheckIcon size={20} />
          {t("actions.closeMedicalLeave")}
        </Button>,
      );
    }
    if (buttons.length === 0) return undefined;
    return <div className="flex flex-col gap-3">{buttons}</div>;
  }

  const start = leave ? new Date(leave.start_date) : null;

  return (
    <>
      {prompt && (
        <Prompt title={prompt.title} text={prompt.text} variant={prompt.variant || "success"} onClose={handlePromptClose} namespace="LeaveApplicationsPage" />
      )}

      {showClose && (
        <CloseMedicalLeaveModal
          leaveId={leaveId}
          minDate={start}
          defaultEndDate={leave?.end_date ? new Date(leave.end_date) : null}
          onClose={() => setShowClose(false)}
          onSuccess={() => {
            setShowClose(false);
            setPrompt({ title: t("actions.successTitle"), text: t("actions.closeSuccess"), variant: "success" });
          }}
        />
      )}

      <ModalForm icon={DocumentText1} onClose={onClose} resize={false} footer={renderFooter()}>
        <div className="flex flex-col gap-6 h-full min-h-0">
          {isLoading || !leave ? (
            <div className="flex flex-col gap-3 py-4">
              <Skeleton height={28} width={240} />
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={20} width="100%" />
              ))}
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
                <h4 className="text-display-2xs content-base-primary">{t("details.medicalLeave")}</h4>
              </div>

              <div className="flex-1 p-1">
                <div className="flex flex-col">
                  <DetailFieldRow label={t("details.worker")} value={leave.worker.full_name} icon={User} />
                  <DetailFieldRow label={t("details.startDate")} value={formatDateForDisplay(leave.start_date, false)} icon={Calendar} />
                  <DetailFieldRow label={t("details.endDate")} value={formatDateForDisplay(leave.end_date, false)} icon={Calendar} />
                  <DetailFieldRow label={t("details.daysCount")} value={String(leave.days_count || 0)} icon={Clock} />
                  {leave.diagnosis ? <DetailFieldRow label={t("details.diagnosis")} value={leave.diagnosis} icon={DocumentText1} /> : null}
                  {leave.sick_note_number ? <DetailFieldRow label={t("details.sickNoteNumber")} value={leave.sick_note_number} icon={DocumentText1} /> : null}
                  <DetailFieldRow label={t("details.status")} value="" icon={DocumentText1} isStatus statusComponent={renderStatusBadge()} isLast />

                  {leave.application_review_note ? (
                    <div className="py-3 border-t surface-base-stroke mt-2">
                      <span className="text-body-regular-md content-base-secondary">{t("details.revisionNote")}</span>
                      <p className="text-body-bold-md content-base-primary mt-1">{leave.application_review_note}</p>
                    </div>
                  ) : null}

                  {leave.benefit_gross_total ? (
                    <div className="py-4 border-t surface-base-stroke mt-2 flex flex-col gap-2">
                      <span className="text-body-bold-md content-base-primary">{t("details.benefit")}</span>
                      <div className="flex justify-between">
                        <span className="text-body-regular-md content-base-secondary">{t("details.benefitAverageDaily")}</span>
                        <span className="text-body-bold-md content-base-primary">{formatMoneyKzt(leave.benefit_average_daily || "0")}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-body-regular-md content-base-secondary">{t("details.benefitWorkingDays")}</span>
                        <span className="text-body-bold-md content-base-primary">{leave.benefit_working_days ?? 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-body-regular-md content-base-secondary">{t("details.benefitTotal")}</span>
                        <span className="text-body-bold-md content-base-primary">{formatMoneyKzt(leave.benefit_gross_total)}</span>
                      </div>
                    </div>
                  ) : null}

                  {leave.certificate_pdf_url ? (
                    <div className="py-3 flex flex-col gap-3 border-t surface-base-stroke mt-2">
                      <span className="text-body-regular-md content-base-secondary">{t("details.sickNoteFileLabel")}</span>
                      <FileViewer existingFileUrl={leave.certificate_pdf_url} />
                    </div>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </div>
      </ModalForm>
    </>
  );
}
